import { ROUTES } from '../../../constants/routes';
import { ReactComponent as MyTimesheetsIcon } from '../../../assets/icons/my-timesheets.svg';
import { ReactComponent as ProxyIcon } from '../../../assets/icons/proxy.svg';
import { ReactComponent as ManagerIcon } from '../../../assets/icons/manager.svg';
import { ReactComponent as DealOwnerIcon } from '../../../assets/icons/deal-owner.svg';
import { ReactComponent as AccountantIcon } from '../../../assets/icons/accountant.svg';
import { ReactComponent as AdministrationIcon } from '../../../assets/icons/administration.svg';
import { User, UserRole } from '../../../types';
import { hasRole, rolesOf } from '../../../utils/roles';

/**
 * Role-named labels for the top-level menu groups. When a user holds more than one role, the generic
 * group titles ("Timesheets", "OP Timesheets", ...) become ambiguous (several groups read the same),
 * so each group is instead labelled by what it contains ("Team Timesheets", "Deal Timesheets", ...).
 * Single-role users keep the original, friendlier titles.
 */
const ROLE_MENU_LABEL: Record<UserRole, string> = {
  [UserRole.OPERATING_PARTNER]: 'My Timesheets',
  [UserRole.PROXY]: 'Timesheets for Others',
  [UserRole.OP_MANAGER]: 'Approvals',
  [UserRole.DEAL_OWNER]: 'Deal Timesheets',
  [UserRole.ACCOUNTANT]: 'Accounting Review',
  [UserRole.ADMIN]: 'As Admin',
};

export const getMenuItems = (user: User | null) => {
  // Multi-role: each top-level group renders iff the user HOLDS that role, so a user with several roles
  // sees several groups (the collapsible-per-role model). Single-role users are unchanged.
  const multiRole = rolesOf(user).length > 1;
  // For multi-role users, label each group by its role so the parent items are unambiguous.
  const groupTitle = (role: UserRole, singleRoleTitle: string) =>
    multiRole ? ROLE_MENU_LABEL[role] : singleRoleTitle;

  return [
    // op
    {
      icon: <MyTimesheetsIcon />,
      route: `/${ROUTES.PENDING_TIMESHEETS}`,
      id: ROUTES.PENDING_TIMESHEETS,
      title: groupTitle(UserRole.OPERATING_PARTNER, 'My Timesheets'),
      show: hasRole(user, UserRole.OPERATING_PARTNER),
      children: [
        {
          route: `/${ROUTES.PENDING_TIMESHEETS}`,
          id: `${ROUTES.PENDING_TIMESHEETS}`,
          title: 'Pending Timesheets',
          show: true,
        },
        {
          route: `/${ROUTES.ALL_TIMESHEETS}`,
          id: `${ROUTES.ALL_TIMESHEETS}`,
          title: 'All Timesheets',
          show: true,
        },
        {
          route: `/${ROUTES.TIMESHEETS_SUMMARY}`,
          id: `${ROUTES.TIMESHEETS_SUMMARY}`,
          title: 'Timesheets Summary',
          show: true,
        },
      ],
    },
    // proxy
    {
      icon: <ProxyIcon />,
      route: `/${ROUTES.ISSUED_TIMESHEETS}`,
      id: ROUTES.ISSUED_TIMESHEETS,
      title: groupTitle(UserRole.PROXY, 'Timesheets'),
      show: hasRole(user, UserRole.PROXY),
      children: [
        {
          route: `/${ROUTES.ISSUED_TIMESHEETS}`,
          id: `${ROUTES.ISSUED_TIMESHEETS}`,
          title: 'Issued Timesheets',
          show: true,
        },
        {
          // Proxy's "All Timesheets" has its own route so it stays independently selectable from the
          // Submitter's "All Timesheets" when a user holds both roles (they share the same page, but
          // the route decides which view/dataset — proxied vs own — is shown).
          route: `/${ROUTES.PROXY_ALL_TIMESHEETS}`,
          id: `${ROUTES.PROXY_ALL_TIMESHEETS}`,
          title: 'All Timesheets',
          show: true,
        },
        {
          route: `/${ROUTES.PROXY_TIMESHEETS_SUMMARY}`,
          id: `${ROUTES.PROXY_TIMESHEETS_SUMMARY}`,
          title: 'Timesheets Summary',
          show: true,
        },
      ],
    },
    //op manager
    {
      icon: <ManagerIcon />,
      route: `/${ROUTES.OP_MANAGER_SUBMITTED_TIMESHEET}`,
      id: ROUTES.OP_MANAGER_SUBMITTED_TIMESHEET,
      title: groupTitle(UserRole.OP_MANAGER, 'OP Timesheets'),
      show: hasRole(user, UserRole.OP_MANAGER),
      children: [
        {
          route: `/${ROUTES.OP_MANAGER_SUBMITTED_TIMESHEET}`,
          id: `${ROUTES.OP_MANAGER_SUBMITTED_TIMESHEET}`,
          title: 'Submitted Timesheets',
          show: true,
        },
        {
          route: `/${ROUTES.OP_MANAGER_ALL_TIMESHEETS}`,
          id: `${ROUTES.OP_MANAGER_ALL_TIMESHEETS}`,
          title: 'All Timesheets',
          show: true,
        },
        {
          route: `/${ROUTES.OP_MANAGER_ALL_OPERATING_PARTNERS}`,
          id: `${ROUTES.OP_MANAGER_ALL_OPERATING_PARTNERS}`,
          title: 'All Submitters',
          show: true,
        },
        {
          route: `/${ROUTES.OP_MANAGER_TIMESHEETS_SUMMARY}`,
          id: `${ROUTES.OP_MANAGER_TIMESHEETS_SUMMARY}`,
          title: 'Timesheets Summary',
          show: true,
        },
      ],
    },
    //
    //deal owner
    {
      icon: <DealOwnerIcon />,
      route: `/${ROUTES.DEAL_OWNER_PENDING_TIMESHEETS}`,
      id: ROUTES.DEAL_OWNER_PENDING_TIMESHEETS,
      title: groupTitle(UserRole.DEAL_OWNER, 'Timesheets'),
      show: hasRole(user, UserRole.DEAL_OWNER),
      children: [
        {
          route: `/${ROUTES.DEAL_OWNER_PENDING_TIMESHEETS}`,
          id: `${ROUTES.DEAL_OWNER_PENDING_TIMESHEETS}`,
          title: 'Pending Timesheets',
          show: true,
        },
        {
          route: `/${ROUTES.DEAL_OWNER_ALL_TIMESHEETS}`,
          id: `${ROUTES.DEAL_OWNER_ALL_TIMESHEETS}`,
          title: 'All Timesheets',
          show: true,
        },
        {
          route: `/${ROUTES.DEAL_OWNER_DEALS}`,
          id: `${ROUTES.DEAL_OWNER_DEALS}`,
          title: 'All Deals',
          show: true,
        },
        {
          route: `/${ROUTES.DEAL_OWNER_OP_CHARGE_SUMMARY}`,
          id: `${ROUTES.DEAL_OWNER_OP_CHARGE_SUMMARY}`,
          title: 'OP Charge Summary',
          show: true,
        },
      ],
    },
    //accountant
    {
      icon: <AccountantIcon />,
      route: `/${ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}`,
      id: ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES,
      title: groupTitle(UserRole.ACCOUNTANT, 'Timesheets'),
      show: hasRole(user, UserRole.ACCOUNTANT),
      children: [
        {
          route: `/${ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}`,
          id: `${ROUTES.ACCOUNTANT_TIMESHEETS_STATUSES}`,
          title: 'Timesheet Statuses',
          show: true,
        },
        {
          route: `/${ROUTES.TIMESHEET_APPROVALS}`,
          id: `${ROUTES.TIMESHEET_APPROVALS}`,
          title: 'Timesheet Approvals',
          show: hasRole(user, UserRole.ADMIN),
        },
        {
          route: `/${ROUTES.ACCOUNTANT_OP_OVERVIEW}`,
          id: `${ROUTES.ACCOUNTANT_OP_OVERVIEW}`,
          title: 'Submitters Overview',
          show: true,
        },
      ],
    },
    // admin permissions
    {
      icon: <AdministrationIcon />,
      route: `/${ROUTES.TIMESHEETS_ASSIGNMENT}`,
      id: ROUTES.TIMESHEETS_ASSIGNMENT,
      title: groupTitle(UserRole.ADMIN, 'Administration'),
      show: hasRole(user, UserRole.ADMIN),
      children: [
        {
          route: `/${ROUTES.TIMESHEETS_ASSIGNMENT}`,
          id: `${ROUTES.TIMESHEETS_ASSIGNMENT}`,
          title: 'Timesheet Assignment',
          show: true,
        },
        {
          route: `/${ROUTES.PROXY_ASSIGNMENT}`,
          id: `${ROUTES.PROXY_ASSIGNMENT}`,
          title: 'Proxy Assignment',
          show: true,
        },
        {
          route: `/${ROUTES.DEALS}`,
          id: `${ROUTES.DEALS}`,
          title: 'Deals',
          show: true,
        },
        {
          route: `/${ROUTES.USER_ADMINISTRATION}`,
          id: `${ROUTES.USER_ADMINISTRATION}`,
          title: 'User Administration',
          show: true,
        },
        {
          route: `/${ROUTES.WORKFLOWS}`,
          id: `${ROUTES.WORKFLOWS}`,
          title: 'Workflows',
          show: true,
        },
        {
          route: `/${ROUTES.APPROVAL_DELEGATES}`,
          id: `${ROUTES.APPROVAL_DELEGATES}`,
          title: 'Approval Delegates',
          show: true,
        },
        {
          route: `/${ROUTES.EVENT_SCHEDULE}`,
          id: `${ROUTES.EVENT_SCHEDULE}`,
          title: 'Event Schedule',
          show: true,
        }
      ],
    },
    //
  ];
};
